import { Injectable } from '@angular/core'
import { SearchUtils } from 'src/app/shared/utils/searchUtils'
import { ClientIPTreeCategory, ClientIPTreeNode } from '../../types/ClientIPListTree'
import {
  ClientIPSetting,
  ClientIPSettingResult,
  ClientIPSettingResultType,
  ClientIPSettings,
} from '../../types/ClientIPListSettings'

@Injectable({
  providedIn: 'root',
})
export class ClientIpListTreeNodesSettingsService {
  useSettingsOnTreeNodes(nodes: ClientIPTreeNode[], settings: ClientIPSettings): ClientIPTreeNode[] {
    if (nodes.length === 0) {
      return []
    }
    return nodes
      .map((node: ClientIPTreeNode) => {
        const { children, ...rest } = node

        const nodeMatchesResult = this.matchesSettings(node, settings)

        // Even if parent should have child that satisfies setting it will not be displayed
        if (
          nodeMatchesResult.type === ClientIPSettingResultType.SETTING_FAILED &&
          (nodeMatchesResult.setting === ClientIPSetting.SHOW_COMPLETED_GOALS ||
            nodeMatchesResult.setting === ClientIPSetting.SHOW_CLOSED_IP ||
            nodeMatchesResult.setting === ClientIPSetting.SHOW_ARCHIVED_PROGRAMS ||
            nodeMatchesResult.setting === ClientIPSetting.SHOW_TERMINATED_PROGRAMS_ENROLLMENTS ||
            nodeMatchesResult.setting === ClientIPSetting.SHOW_ARCHIVED_SUBPROGRAMS ||
            nodeMatchesResult.setting === ClientIPSetting.SHOW_TERMINATED_SUBPROGRAMS_ENROLLMENTS ||
            nodeMatchesResult.setting === ClientIPSetting.DUE_DATE)
        ) {
          return null
        }

        const filteredChildren = this.useSettingsOnTreeNodes(children || [], settings)
        const hasMatchingChildren = filteredChildren.length > 0

        if (nodeMatchesResult.type === ClientIPSettingResultType.SUCCESS || hasMatchingChildren) {
          return {
            ...rest,
            children: filteredChildren,
            expanded: true,
          }
        }

        return null
      })
      .filter((node: ClientIPTreeNode) => node !== null)
  }

  /**
   * See if node matches all settings, if not it returns on which setting it failed
   * @param node to be matched against settings
   * @param settings provided from user
   */
  private matchesSettings(node: ClientIPTreeNode, settings: ClientIPSettings): ClientIPSettingResult {
    if (!SearchUtils.includeFilter(node.label, settings.search)) {
      return { type: ClientIPSettingResultType.SETTING_FAILED, setting: ClientIPSetting.SEARCH }
    }
    if (
      node.data.type === ClientIPTreeCategory.STEP &&
      !this.satisfiesShouldShowCompletedTooSetting(settings.showCompletedSteps, node.data.isCompleted)
    ) {
      return { type: ClientIPSettingResultType.SETTING_FAILED, setting: ClientIPSetting.SHOW_COMPLETED_STEPS }
    }
    if (
      node.data.type === ClientIPTreeCategory.GOAL &&
      !this.satisfiesShouldShowCompletedTooSetting(settings.showCompletedGoals, node.data.isCompleted)
    ) {
      return { type: ClientIPSettingResultType.SETTING_FAILED, setting: ClientIPSetting.SHOW_COMPLETED_GOALS }
    }
    if (
      node.data.type === ClientIPTreeCategory.INDIVIDUAL_PLAN &&
      !this.satisfiesShouldShowCompletedTooSetting(settings.showClosedIP, node.data.isCompleted)
    ) {
      return { type: ClientIPSettingResultType.SETTING_FAILED, setting: ClientIPSetting.SHOW_CLOSED_IP }
    }
    if (
      node.data.type === ClientIPTreeCategory.SUBPROGRAM_ENROLLMENT &&
      !this.satisfiesShouldShowCompletedTooSetting(settings.showArchivedSubprograms, node.data.isCompleted)
    ) {
      return {
        type: ClientIPSettingResultType.SETTING_FAILED,
        setting: ClientIPSetting.SHOW_ARCHIVED_SUBPROGRAMS,
      }
    }
    if (
      node.data.type === ClientIPTreeCategory.SUBPROGRAM_ENROLLMENT &&
      !this.satisfiesShouldShowCompletedTooSetting(
        settings.showTerminatedSubprogramsEnrollments,
        node.data.isTerminated,
      )
    ) {
      return {
        type: ClientIPSettingResultType.SETTING_FAILED,
        setting: ClientIPSetting.SHOW_TERMINATED_SUBPROGRAMS_ENROLLMENTS,
      }
    }
    if (
      node.data.type === ClientIPTreeCategory.PROGRAM_ENROLLMENT &&
      !this.satisfiesShouldShowCompletedTooSetting(settings.showArchivedPrograms, node.data.isCompleted)
    ) {
      return {
        type: ClientIPSettingResultType.SETTING_FAILED,
        setting: ClientIPSetting.SHOW_ARCHIVED_PROGRAMS,
      }
    }
    if (
      node.data.type === ClientIPTreeCategory.PROGRAM_ENROLLMENT &&
      !this.satisfiesShouldShowCompletedTooSetting(settings.showTerminatedProgramsEnrollments, node.data.isTerminated)
    ) {
      return {
        type: ClientIPSettingResultType.SETTING_FAILED,
        setting: ClientIPSetting.SHOW_TERMINATED_PROGRAMS_ENROLLMENTS,
      }
    }
    if (!this.isBeforeDate(node.data.dueDate, settings.dueDate)) {
      return { type: ClientIPSettingResultType.SETTING_FAILED, setting: ClientIPSetting.DUE_DATE }
    }

    return { type: ClientIPSettingResultType.SUCCESS }
  }

  private satisfiesShouldShowCompletedTooSetting(
    userFilledSetting: boolean, // what user newly set as setting
    isCompletedData: boolean, // what data has set
  ): boolean {
    if (!isCompletedData) {
      return true
    } else if (isCompletedData && userFilledSetting) {
      return true
    }
    return false
  }

  private isBeforeDate(dueDate: Date | undefined, dueDateSetting: Date | undefined | null) {
    if (!dueDate || !dueDateSetting) {
      return true
    }
    return new Date(dueDate.setHours(0, 0, 0, 0)).getTime() <= dueDateSetting.getTime()
  }
}
